/**
 * Redirect Handler Service
 * Handles the redirection when a user clicks on a shortened URL
 */

import { getUrlByShortCode, incrementUrlClicks } from "./airtable";

/**
 * Process a redirect based on the short code in the URL path
 * @returns {Promise<boolean>} - Whether a redirect was performed
 */
export const handleRedirect = async () => {
  console.log("Redirect handler executing...");

  // Get the current path and hostname
  const path = window.location.pathname;
  const hostname = window.location.hostname;
  console.log(`Current path: ${path}, hostname: ${hostname}`);

  // Check if we're on the link domain
  const isLinkDomain = hostname.includes("link.richs.my");
  console.log(`Is link domain: ${isLinkDomain}`);

  // If path is just a slash and we're on the link domain, redirect to richs.com.my
  if ((path === "/" || path === "") && isLinkDomain) {
    console.log("Base URL of link domain accessed, redirecting to richs.com.my");
    window.location.replace("https://richs.com.my");
    return true;
  }

  // If path is just a slash and we're NOT on the link domain, no redirect needed
  if (path === "/" || path === "") {
    console.log("Home page detected on app domain, no redirect needed");
    return false;
  }

  // Special debug mode to test click counter without redirecting
  if (path.startsWith("/debug/")) {
    const debugShortCode = path.substring(7); // Remove /debug/
    console.log(`Debug mode: testing click counter for code ${debugShortCode}`);

    try {
      const urlData = await getUrlByShortCode(debugShortCode);
      if (urlData) {
        console.log(`Found URL record:`, urlData);
        const result = await incrementUrlClicks(urlData.id);
        console.log(`Click increment result:`, result);
        return false; // Don't redirect
      } else {
        console.log(`URL not found for debug code: ${debugShortCode}`);
      }
    } catch (error) {
      console.error(`Debug error:`, error);
    }

    return false; // Don't redirect after debug
  }

  // Extract the short code from the path (remove leading slash)
  let shortCode = path.substring(1);

  // Handle development paths that might include /link/ prefix
  if (shortCode.startsWith("link/")) {
    shortCode = shortCode.substring(5);
  }

  console.log(`Extracted short code: ${shortCode}`);

  if (!shortCode) {
    console.log("No short code found in URL");
    return false;
  }

  try {
    console.log(`Checking redirect for short code: ${shortCode}`);

    // Look up the short code in Airtable
    console.log(`Fetching URL data from Airtable for short code: ${shortCode}`);
    const urlData = await getUrlByShortCode(shortCode);
    console.log(`URL data received:`, urlData ? "Data found" : "No data found");

    // If the URL was found and is active
    if (urlData && urlData.status !== "Deleted") {
      console.log(`URL record found with ID: ${urlData.id}`);
      console.log(`URL status: ${urlData.status}`);
      console.log(`Target URL: ${urlData.fullutmurl || urlData.originalurl}`);

      // Increment click counter in non-blocking way
      incrementUrlClicks(urlData.id)
        .then(() =>
          console.log(`Successfully incremented click count for ${shortCode}`)
        )
        .catch((err) =>
          console.error(`Failed to increment clicks: ${err.message}`)
        );

      // Redirect to the full UTM URL if available, otherwise use the original URL
      const redirectUrl = urlData.fullutmurl || urlData.originalurl;

      console.log(`Initiating redirect to: ${redirectUrl}`);

      // Force immediate redirect
      window.location.replace(redirectUrl);

      // Use a fallback timeout in case replace doesn't work immediately
      setTimeout(() => {
        console.log("Fallback redirect triggered");
        window.location.href = redirectUrl;
      }, 100);

      return true;
    } else {
      // URL not found or deleted
      console.log(`Short URL not found or inactive: ${shortCode}`);
      
      // If on link domain, redirect to main website for invalid shortcodes
      if (isLinkDomain) {
        console.log("Redirecting to richs.com.my for invalid shortcode");
        window.location.replace("https://richs.com.my");
        return true;
      }
      
      return false;
    }
  } catch (error) {
    console.error("Error during redirect process:", error);
    
    // If on link domain, redirect to main website on error
    if (isLinkDomain) {
      console.log("Redirecting to richs.com.my after error");
      window.location.replace("https://richs.com.my");
      return true;
    }
    
    return false;
  }
};

/**
 * Setup redirect listener to check for redirects when the app loads
 * This should be called in the app initialization
 */
export const setupRedirectListener = async () => {
  console.log("Setting up redirect listener...");
  try {
    const redirected = await handleRedirect();
    console.log(`Redirect check complete. Redirected: ${redirected}`);
    return redirected;
  } catch (err) {
    console.error("Error in redirect listener:", err);
    return false;
  }
};

export default {
  handleRedirect,
  setupRedirectListener,
};
